import React, { useEffect, useState } from "react";
import twFocusClass from "utils/twFocusClass";
import axios from "axios";
import "../../../styles/styles.css";

import { Video } from "data/types";
import CardVideo from "./CardVideo";

const API_URL = process.env.REACT_APP_API_URL;
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: { "Content-Type": "application/json" },
});

const totalParPage = 3; // 3 vidéos par page par défaut

interface PageArchiveVideoProps {
  nombre?: number; // nombre de vidéos à afficher sans pagination
}

const PageArchiveVideo: React.FC<PageArchiveVideoProps> = ({ nombre }) => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [screenSize, setScreenSize] = useState<"mobile" | "tablet" | "desktop">("desktop");

  // Récupération des vidéos
  useEffect(() => {
    const getVideos = async () => {
      try {
        const response = await axiosInstance.get("api/videos");

        const sortedVideos = response.data.sort(
          (a: Video, b: Video) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setVideos(sortedVideos);
      } catch (error) {
        console.log("erreur:", error);
      }
    };

    getVideos();
  }, []);

  // Détection de la taille de l'écran
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) setScreenSize("mobile");
      else if (width >= 640 && width < 1024) setScreenSize("tablet");
      else setScreenSize("desktop");
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Choix des vidéos à afficher
  const displayedVideos = nombre ? videos.slice(0, nombre) : videos;

  // Pagination
  const loadPageData = (page: number, list: Video[]): Video[] => {
    const start = (page - 1) * totalParPage;
    return list.slice(start, start + totalParPage);
  };

  const paginatedVideos =
    screenSize === "mobile" || nombre
      ? displayedVideos
      : loadPageData(currentPage, displayedVideos);

  const gotoPage = (i: number) => setCurrentPage(i);

  // Boutons de pagination (tablette et desktop uniquement)
  const getPagesNumbers = () => {
    if (screenSize === "mobile" || nombre) return null;
    const totalPages = Math.ceil(displayedVideos.length / totalParPage);
    const content = [];
    for (let i = 1; i <= totalPages; i++) {
      content.push(
        <button
          key={i}
          onClick={i === currentPage ? undefined : () => gotoPage(i)}
          className={`inline-flex w-11 h-11 items-center justify-center rounded-full ${
            i === currentPage
              ? "bg-primary-6000 text-white"
              : "bg-white hover:bg-neutral-100 border border-neutral-200 text-neutral-6000 dark:text-neutral-400 dark:bg-neutral-900 dark:hover:bg-neutral-800 dark:border-neutral-700"
          } ${twFocusClass()}`}
        >
          {i}
        </button>
      );
    }
    return content;
  };

  return (
    <div className="stretch bgColor">
      <div className="container py-3 lg:py-4">
        {/* Titre */}
        <h2 className="inline-block align-middle text-4xl font-semibold md:text-5xl">
          Vidéos
        </h2>

        {/* Texte du nombre et bouton collé */}
        {nombre ? (
          <div className="flex items-center gap-3 mt-0">
            <span className="text-black">Les {nombre} plus récentes vidéos</span>
            <a
              href="/videos"
              className="inline-block px-3 py-1 text-white bg-primary-6000 rounded-full hover:bg-primary-700 transition-colors duration-200 text-sm"
            >
              Voir plus de vidéos
            </a>
          </div>
        ) : (
          <span className="block text-black mt-2">{videos.length} vidéos</span>
        )}

        {/* Grille des vidéos */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-4 md:gap-x-4 md:gap-y-5 mt-4 lg:mt-5">
          {paginatedVideos.map((video: Video) => (
            <CardVideo key={video.id} video={video} />
          ))}
        </div>

        {/* Pagination */}
        <div className="mt-6 flex justify-center gap-2">{getPagesNumbers()}</div>
      </div>
    </div>
  );
};

export default PageArchiveVideo;
